import { ref, computed, readonly } from 'vue';

/**
 * Pengelolaan tema terang / gelap.
 *
 * Tiga mode:
 *   'light'  — selalu terang
 *   'dark'   — selalu gelap
 *   'system' — mengikuti pengaturan sistem operasi (bawaan)
 *
 * Pilihan disimpan di localStorage. Tema sebenarnya diterapkan sebagai
 * atribut data-theme pada <html>, yang dibaca oleh assets/css/theme.css.
 */

const STORAGE_KEY = 'arspo:theme';
/* Kunci sebelum penggantian merek. Dibaca sekali lalu dipindahkan
   supaya pilihan pengguna yang sudah tersimpan tidak hilang. */
const STORAGE_KEY_LAMA = 'cendrawasih:theme';
const VALID_MODES = ['light', 'dark', 'system'];

// State modul — dibagi ke semua komponen yang memanggil useTheme().
const mode = ref('system');
const systemPrefersDark = ref(false);

let initialised = false;
let mediaQuery = null;

function resolveTheme() {
  if (mode.value === 'system') {
    return systemPrefersDark.value ? 'dark' : 'light';
  }
  return mode.value;
}

function applyTheme() {
  const theme = resolveTheme();
  const root = document.documentElement;

  root.setAttribute('data-theme', theme);

  /*
   * Menyesuaikan warna bilah alamat peramban di ponsel.
   *
   * index.html memasang DUA meta theme-color dengan media query, supaya warna
   * bilah alamat sudah benar sejak cat pertama — sebelum JavaScript sempat
   * jalan. Di sini keduanya ditimpa dengan nilai yang sama, karena pilihan
   * eksplisit pengguna harus mengalahkan preferensi sistem operasinya.
   *
   * Nilainya diambil dari --bg-base di theme.css. Sebelumnya tertulis
   * #020617 — biru kehitaman sisa tema navy lama — sehingga bilah alamat
   * berwarna biru sementara halamannya cokelat kehitaman.
   */
  const warna = theme === 'dark' ? '#100F0E' : '#FAF8F6';
  document.querySelectorAll('meta[name="theme-color"]').forEach((meta) => {
    meta.setAttribute('content', warna);
  });
}

function handleSystemChange(event) {
  systemPrefersDark.value = event.matches;
  if (mode.value === 'system') applyTheme();
}

function initTheme() {
  if (initialised || typeof window === 'undefined') return;
  initialised = true;

  mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  systemPrefersDark.value = mediaQuery.matches;

  // addEventListener tidak tersedia di Safari lama, jadi ada fallback.
  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener('change', handleSystemChange);
  } else if (mediaQuery.addListener) {
    mediaQuery.addListener(handleSystemChange);
  }

  try {
    let saved = localStorage.getItem(STORAGE_KEY);

    // Pindahkan pilihan yang tersimpan di bawah kunci merek lama.
    if (saved === null) {
      const lama = localStorage.getItem(STORAGE_KEY_LAMA);
      if (lama !== null) {
        saved = lama;
        localStorage.setItem(STORAGE_KEY, lama);
        localStorage.removeItem(STORAGE_KEY_LAMA);
      }
    }
    if (saved && VALID_MODES.includes(saved)) {
      mode.value = saved;
    }
  } catch (error) {
    // Mode penyamaran memblokir localStorage — abaikan, pakai bawaan sistem.
  }

  applyTheme();
}

function setTheme(next) {
  if (!VALID_MODES.includes(next)) return;

  mode.value = next;
  applyTheme();

  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch (error) {
    // Gagal menyimpan bukan alasan untuk membatalkan pergantian tema.
  }
}

/**
 * Berganti langsung antara terang dan gelap.
 * Kalau sedang mengikuti sistem, hasilnya dibalik dari tema yang aktif.
 */
function toggleTheme() {
  setTheme(resolveTheme() === 'dark' ? 'light' : 'dark');
}

export function useTheme() {
  initTheme();

  return {
    mode: readonly(mode),
    activeTheme: computed(resolveTheme),
    isDark: computed(() => resolveTheme() === 'dark'),
    setTheme,
    toggleTheme,
  };
}

/**
 * Dipanggil di main.js sebelum aplikasi dipasang, supaya halaman tidak
 * sempat berkedip putih sebelum tema gelap diterapkan.
 */
export { initTheme };
